import type { CommunityOperatorSectionData } from '@/lib/mock/communityOperator'
import {
  berlinCommunityOperator,
  berlinCommunityOperatorSlides,
} from '@/lib/mock/communityOperator'
import type { LocationPropertyChooser } from '@/lib/mock/locationPages'
import { berlinPropertyChooser } from '@/lib/mock/locationPages'
import { isCityId, mapCities } from '@/lib/mock/mapLocations'

export type CityOverviewStat = {
  value: string
  label: string
}

export type CityOverviewPolaroid = {
  id: string
  image: string
  imageAlt: string
  title?: string
  caption?: string
  captionLine1?: string
  captionLine2?: string
  captionAlign?: 'left' | 'center' | 'right'
  detailText?: string
  playAriaLabel?: string
  autoplayVideo?: boolean
  videoUrl?: string
  videoMimeType?: string
}

export type CityJourneyImage = {
  id: string
  src: string
  alt: string
}

export type CityJourney = {
  titleLine1: string
  titleLine2: string
  titleHighlight: string
  backgroundImage: string
  backgroundImageAlt: string
  decorativeImage: string
  decorativeImageAlt: string
  playAriaLabel: string
  videoModalTitle: string
  videoModalCloseLabel: string
  videoComingSoonLabel: string
  galleryAriaLabel: string
  galleryScrollPrevious: string
  galleryScrollNext: string
  images: CityJourneyImage[]
  videoUrl?: string
  videoMimeType?: string
}

export type CityAreaMapParagraph = {
  lead?: string
  text: string
}

export type CityAreaMapCameraPadding = {
  bottom?: number
  centerLatOffset?: number
  centerLngOffset?: number
  contentGap?: number
  /** Use only the configured inset values instead of measuring the content column. */
  disableContentInset?: boolean
  extraLeft?: number
  leftMaxRatio?: number
  minLeft?: number
  /** Fit overview markers at the current zoom instead of the static overview center. */
  overviewFitAtZoom?: boolean
  right?: number
  top?: number
}

export type CityAreaMap = {
  zoom: number
  mapCameraPadding?: CityAreaMapCameraPadding
  eyebrow?: string
  titleBeforeHighlight: string
  titleHighlight: string
  paragraphs: CityAreaMapParagraph[]
  ctaLabel: string
  ctaHref: string
  mapAlt: string
  mapUnavailable: string
}

export type CityOverview = {
  titleBeforeHighlight: string
  titleHighlight: string
  stats: CityOverviewStat[]
  bodyParagraphs: string[]
  polaroids: CityOverviewPolaroid[]
  polaroidGalleryAriaLabel: string
  polaroidScrollPrevious: string
  polaroidScrollNext: string
  playVideoLabel: string
  polaroidAutoplayPlayLabel: string
  polaroidAutoplayPauseLabel: string
  polaroidDetailCloseLabel: string
  videoModalTitle: string
  videoModalCloseLabel: string
  videoComingSoonLabel: string
}

export type City = {
  slug: string
  name: string
  description: string
  heroImage: string
  heroImageAlt: string
  heroVideoDesktop: string
  heroVideoMobile: string
  previewImage: string
  previewImageAlt: string
  primaryCtaLabel: string
  primaryCtaHref: string
  secondaryCtaLabel: string
  secondaryCtaHref: string
  promotionText: string
  promotionCtaLabel: string
  promotionCtaHref: string
  overview: CityOverview
  journey: CityJourney
  areaMap: CityAreaMap
  communityOperator: CommunityOperatorSectionData
  propertyChooser?: LocationPropertyChooser
}

const berlinOverview: CityOverview = {
  titleBeforeHighlight: 'Neon Wood – Berlin\u2019s ',
  titleHighlight: 'largest private operator of student housing',
  stats: [
    { value: '7', label: 'properties across 4 German cities' },
    { value: '2,000+', label: 'Apartments Ready to Move In' },
    { value: '2,200+', label: 'residents from > 60 nationalities' },
  ],
  bodyParagraphs: [
    'Neon Wood is currently represented in the German capital with 3 locations, Neon Wood Berlin Frankfurter Tor, Neon Wood Berlin Mitte-Wedding and Neon Wood Berlin Adlershof.',
    'The cultural metropolis of Berlin is an open-minded city with lots of culture - ideal for your studies. Whatever interests you, you will find it in the captal city. No matter if architecture, business administration, GEO-sciences, higher management, art, music, or psychology. In Berlin\'s large public and private universities and colleges you can study anything that suits your ideas. With our excellent universities you will give your career a big jump.',
  ],
  polaroidGalleryAriaLabel: 'Berlin student life photo gallery',
  polaroidScrollPrevious: 'Previous photo',
  polaroidScrollNext: 'Next photo',
  playVideoLabel: 'Play',
  polaroidAutoplayPlayLabel: 'Play carousel',
  polaroidAutoplayPauseLabel: 'Pause carousel',
  polaroidDetailCloseLabel: 'Close details',
  videoModalTitle: 'Neon Wood video',
  videoModalCloseLabel: 'Close video',
  videoComingSoonLabel: 'Video coming soon',
  polaroids: [
    {
      id: 'vibrant-community',
      caption: 'Vibrant student community',
      image: '/media/cities/berlin/overview/vibrant-community.png',
      imageAlt: 'Students socializing in a bright Neon Wood common area in Berlin',
      captionAlign: 'left',
    },
    {
      id: 'furnished-apartments',
      caption: 'Stylish, fully furnished apartments',
      image: '/media/cities/berlin/overview/furnished-apartments.png',
      imageAlt: 'Modern furnished student apartment interior at Neon Wood Berlin',
      captionAlign: 'center',
    },
    {
      id: 'student-stories',
      caption: 'Hear from students living at Neon Wood',
      image: '/media/cities/berlin/overview/student-stories.png',
      imageAlt: 'Students sharing their Neon Wood Berlin experience',
      playAriaLabel: 'Play student stories video',
      captionAlign: 'left',
    },
    {
      id: 'city-home',
      captionLine1: 'More than four walls',
      captionLine2: 'A city home designed for student life',
      image: '/media/cities/berlin/overview/student-stories.png',
      imageAlt: 'Exterior view of a Neon Wood student residence in Berlin',
      captionAlign: 'center',
    },
    {
      id: 'beat-of-neon-wood',
      caption: 'The Beat of Neon Wood',
      image: '/media/cities/berlin/overview/beat-of-neon-wood.png',
      imageAlt: 'Neon Wood Berlin community event atmosphere',
      playAriaLabel: 'Play The Beat of Neon Wood video',
      captionAlign: 'right',
    },
    {
      id: 'amenities',
      caption: 'Great on-site amenities',
      image: '/media/cities/berlin/overview/amenities.png',
      imageAlt: 'On-site amenities at a Neon Wood Berlin residence',
      captionAlign: 'center',
    },
    {
      id: 'weekly-events',
      caption: 'Weekly events and gaming lounges',
      image: '/media/cities/berlin/overview/vibrant-community.png',
      imageAlt: 'Students enjoying a community event at Neon Wood Berlin',
      captionAlign: 'center',
    },
    {
      id: 'neighborhood-kiez',
      caption: 'Steps from your favorite kiez',
      image: '/media/cities/berlin/overview/student-stories.png',
      imageAlt: 'Neon Wood Berlin residence near a vibrant neighborhood',
      captionAlign: 'right',
    },
    {
      id: 'move-in-abroad',
      caption: 'Book from abroad, move in fast',
      image: '/media/cities/berlin/overview/furnished-apartments.png',
      imageAlt: 'Student arriving at a Neon Wood Berlin apartment',
      captionAlign: 'center',
    },
    {
      id: 'campus-life',
      captionLine1: 'Study, socialize, repeat',
      captionLine2: 'Campus life without the commute',
      image: '/media/cities/berlin/overview/amenities.png',
      imageAlt: 'Students relaxing in a Neon Wood Berlin common space',
      captionAlign: 'center',
    },
    {
      id: 'community-tour',
      caption: 'Take a tour of our Berlin community',
      image: '/media/cities/berlin/overview/beat-of-neon-wood.png',
      imageAlt: 'Tour of Neon Wood Berlin shared spaces',
      playAriaLabel: 'Play Berlin community tour video',
      captionAlign: 'left',
    },
    {
      id: 'study-lounges',
      caption: 'Quiet study lounges when you need focus',
      image: '/media/cities/berlin/overview/furnished-apartments.png',
      imageAlt: 'Students studying in a Neon Wood Berlin lounge',
      captionAlign: 'left',
    },
    {
      id: 'movie-nights',
      caption: 'Movie nights with friends on campus',
      image: '/media/cities/berlin/overview/student-stories.png',
      imageAlt: 'Students watching a film in a Neon Wood Berlin common room',
      captionAlign: 'center',
    },
    {
      id: 'fitness-zone',
      caption: '24/7 fitness just downstairs',
      image: '/media/cities/berlin/overview/amenities.png',
      imageAlt: 'Fitness area at a Neon Wood Berlin residence',
      captionAlign: 'right',
    },
    {
      id: 'welcome-party',
      captionLine1: 'Welcome parties',
      captionLine2: 'for every new semester',
      image: '/media/cities/berlin/overview/vibrant-community.png',
      imageAlt: 'Welcome party for new students at Neon Wood Berlin',
      captionAlign: 'center',
    },
    {
      id: 'berlin-sunset',
      caption: 'Berlin sunsets from the courtyard',
      image: '/media/cities/berlin/overview/city-home.png',
      imageAlt: 'Courtyard view at Neon Wood Berlin at sunset',
      captionAlign: 'right',
    },
  ],
}

const berlinAreaMap: CityAreaMap = {
  eyebrow: 'Choose your Neon Wood city',
  zoom: 11.5,
  mapCameraPadding: {
    centerLngOffset: 0,
    contentGap: 200,
    extraLeft: 120,
    leftMaxRatio: 0.92,
    minLeft: 900,
    right: 14,
  },
  titleBeforeHighlight: 'All your student life essentials ',
  titleHighlight: 'right around the corner',
  paragraphs: [
    {
      lead: 'Want to go out or do something fun on a sunny day?',
      text: 'We\u2019ve picked out the best Parks & Outdoor spots for you.',
    },
    {
      lead: 'Not feeling the sun today?',
      text: 'Then check out Culture & Entertainment, maybe visit a museum or catch a movie, or get a bit of Sports & Fitness in.',
    },
    {
      lead: 'Hungry or craving a coffee?',
      text: 'We\u2019ll show you the best Caf\u00e9s & Bars & Restaurants and where to get Groceries & Essentials so you\u2019re always covered.',
    },
    {
      text: 'And for everything else, we\u2019ve got you sorted too. Find Universities, Banks & Services, Health, as well as Libraries & Co-Working Spaces all in one place, all for you.',
    },
  ],
  ctaLabel: 'Explore the area',
  ctaHref: '#explore-area',
  mapAlt: 'Map of Berlin showing the area around Neon Wood student housing',
  mapUnavailable: 'Map preview is unavailable right now.',
}

export const cityJourneyDiscoverImages: Record<
  string,
  {
    alt: string
    src: string
  }
> = {
  berlin: {
    src: '/media/cities/berlin/journey/discover-background.webp',
    alt: 'Berlin skyline with the TV Tower at dusk',
  },
  cologne: {
    src: '/media/cities/cologne/journey/discover-background.webp',
    alt: 'Cologne Cathedral and the Rhine riverfront at sunset',
  },
  frankfurt: {
    src: '/media/cities/frankfurt/journey/discover-background.webp',
    alt: 'Frankfurt skyline with modern high-rise towers',
  },
  tuebingen: {
    src: '/media/cities/tuebingen/journey/discover-background.webp',
    alt: 'Colorful historic buildings in Tübingen old town',
  },
}

const DEFAULT_CITY_CTAS = {
  primaryCtaLabel: 'Book a Tour',
  primaryCtaHref: 'https://crescocapitalgroup.my.salesforce-sites.com/InspectionCalendar',
  secondaryCtaLabel: 'Explore Apartments',
  secondaryCtaHref: '/apartments',
  promotionText: 'LIMITED PROMOTION: check current offers',
  promotionCtaLabel: 'See promotions',
  promotionCtaHref: '#promotions',
} as const

function createStubCommunityOperator(
  cityName: string,
  bodyParagraphs?: string[],
): CommunityOperatorSectionData {
  return {
    titleBeforeHighlight: 'Student life in ',
    titleHighlight: cityName,
    bodyParagraphs: bodyParagraphs ?? [
      `Neon Wood offers fully furnished student apartments in ${cityName}, with shared community spaces and an on-site team to help you settle in quickly.`,
      `Whether you are arriving from abroad or moving within Germany, you get an all-inclusive home base close to campus, transit, and everyday essentials.`,
    ],
    galleryAriaLabel: `Neon Wood ${cityName} photo gallery`,
    carouselPrevious: 'Previous photo',
    carouselNext: 'Next photo',
    slides: [...berlinCommunityOperatorSlides],
  }
}

function createStubJourney(cityName: string, slug: string): CityJourney {
  const discoverImage = cityJourneyDiscoverImages[slug]

  return {
    titleLine1: 'Discover',
    titleLine2: cityName,
    titleHighlight: 'Discover',
    backgroundImage:
      discoverImage?.src ?? '/media/cities/berlin/journey/hero-background.png',
    backgroundImageAlt: discoverImage?.alt ?? '',
    decorativeImage: '/media/cities/berlin/journey/decorative.png',
    decorativeImageAlt: '',
    playAriaLabel: `Play Discover ${cityName} video`,
    videoModalTitle: `Discover ${cityName}`,
    videoModalCloseLabel: 'Close video',
    videoComingSoonLabel: 'Video coming soon',
    galleryAriaLabel: `${cityName} journey photo gallery`,
    galleryScrollPrevious: 'Previous photo',
    galleryScrollNext: 'Next photo',
    images: [
      {
        id: 'gallery-1',
        src: '/media/homepage/why-neon-wood/city-home.png',
        alt: `Student life in ${cityName} at Neon Wood`,
      },
      {
        id: 'gallery-2',
        src: '/media/homepage/why-neon-wood/vibrant-community.png',
        alt: `Community spaces at Neon Wood ${cityName}`,
      },
      {
        id: 'gallery-3',
        src: '/media/homepage/why-neon-wood/furnished-apartments.png',
        alt: `Furnished student apartment at Neon Wood ${cityName}`,
      },
    ],
  }
}

function createStubAreaMap(cityName: string, zoom: number): CityAreaMap {
  return {
    eyebrow: 'Choose your Neon Wood property',
    zoom,
    titleBeforeHighlight: 'All your student life essentials ',
    titleHighlight: 'right around the corner',
    paragraphs: [
      {
        lead: 'Want to go out or do something fun on a sunny day?',
        text: 'We have picked out parks, cafés, and local favorites around your Neon Wood home.',
      },
      {
        text: 'Find universities, groceries, culture, and everyday services all in one place, all for you.',
      },
    ],
    ctaLabel: 'Explore the area',
    ctaHref: '#explore-area',
    mapAlt: `Map of ${cityName} showing Neon Wood student housing`,
    mapUnavailable: 'Map preview is unavailable right now.',
  }
}

function createStubOverview(cityName: string): CityOverview {
  return {
    titleBeforeHighlight: 'Neon Wood in ',
    titleHighlight: cityName,
    stats: [
      { value: '7', label: 'properties across 4 German cities' },
      { value: '2,000+', label: 'Apartments Ready to Move In' },
      { value: '2,200+', label: 'residents from > 60 nationalities' },
    ],
    bodyParagraphs: [
      `Neon Wood brings furnished student apartments and community life to ${cityName}.`,
    ],
    polaroidGalleryAriaLabel: `${cityName} student life photo gallery`,
    polaroidScrollPrevious: 'Previous photo',
    polaroidScrollNext: 'Next photo',
    playVideoLabel: 'Play',
    polaroidAutoplayPlayLabel: 'Play carousel',
    polaroidAutoplayPauseLabel: 'Pause carousel',
    polaroidDetailCloseLabel: 'Close details',
    videoModalTitle: 'Neon Wood video',
    videoModalCloseLabel: 'Close video',
    videoComingSoonLabel: 'Video coming soon',
    polaroids: [
      {
        id: 'community',
        caption: 'Vibrant student community',
        image: '/media/homepage/why-neon-wood/vibrant-community.png',
        imageAlt: `Students socializing at Neon Wood ${cityName}`,
        captionAlign: 'left',
      },
    ],
  }
}

function createStubCity(
  slug: string,
  name: string,
  options: {
    areaMapZoom?: number
    communityBody?: string[]
    description?: string
    previewImage?: string
    previewImageAlt?: string
  } = {},
): City {
  return {
    slug,
    name,
    description: options.description ?? `Student housing in ${name} by Neon Wood.`,
    heroImage: `/media/cities/${slug}/hero-background.png`,
    heroImageAlt: '',
    heroVideoDesktop: '/media/cities/city-placeholder.mp4',
    heroVideoMobile: '/media/cities/city-placeholder-mobile.mp4',
    previewImage:
      options.previewImage ??
      cityJourneyDiscoverImages[slug]?.src ??
      `/media/cities/${slug}/hero-preview.png`,
    previewImageAlt:
      options.previewImageAlt ??
      cityJourneyDiscoverImages[slug]?.alt ??
      `Neon Wood student housing in ${name}`,
    ...DEFAULT_CITY_CTAS,
    overview: createStubOverview(name),
    journey: createStubJourney(name, slug),
    areaMap: createStubAreaMap(name, options.areaMapZoom ?? 11.5),
    communityOperator: createStubCommunityOperator(name, options.communityBody),
  }
}

const berlinJourney: CityJourney = {
  titleLine1: 'A Journey',
  titleLine2: 'through Berlin',
  titleHighlight: 'A Journey',
  backgroundImage: cityJourneyDiscoverImages.berlin.src,
  backgroundImageAlt: cityJourneyDiscoverImages.berlin.alt,
  decorativeImage: '/media/cities/berlin/journey/decorative.png',
  decorativeImageAlt: '',
  playAriaLabel: 'Play A Journey through Berlin video',
  videoModalTitle: 'A Journey through Berlin',
  videoModalCloseLabel: 'Close video',
  videoComingSoonLabel: 'Video coming soon',
  galleryAriaLabel: 'Berlin journey photo gallery',
  galleryScrollPrevious: 'Previous photo',
  galleryScrollNext: 'Next photo',
  images: [
    {
      id: 'gallery-1',
      src: '/media/cities/berlin/journey/gallery-1.png',
      alt: 'Students relaxing together in a Neon Wood Berlin common area',
    },
    {
      id: 'gallery-2',
      src: '/media/cities/berlin/journey/gallery-4.png',
      alt: 'Berlin street scene near a Neon Wood residence',
    },
    {
      id: 'gallery-3',
      src: '/media/cities/berlin/journey/gallery-2.png',
      alt: 'Student life moment in a Neon Wood Berlin apartment',
    },
    {
      id: 'gallery-4',
      src: '/media/cities/berlin/journey/gallery-3.png',
      alt: 'Community gathering at a Neon Wood Berlin location',
    },
  ],
}

export const cities: Record<string, City> = {
  berlin: {
    slug: 'berlin',
    name: 'Berlin',
    description:
      'Neon Wood is currently represented in the German capital with 3 locations, Neon Wood Berlin Frankfurter Tor, Neon Wood Berlin Mitte-Wedding and Neon Wood Berlin Adlershof.\n\n \n\n The cultural metropolis of Berlin is an open-minded city with lots of culture - ideal for your studies. Whatever interests you, you will find it in the captal city. No matter if architecture, business administration, GEO-sciences, higher management, art, music, psychology or or or... . In Berlin\'s large public and private universities and colleges you can study anything that suits your ideas. With our excellent universities you will give your career a big jump.',
    heroImage: '/media/cities/berlin/hero-background.png',
    heroImageAlt: '',
    heroVideoDesktop: '/media/cities/city-placeholder.mp4',
    heroVideoMobile: '/media/cities/city-placeholder-mobile.mp4',
    previewImage: cityJourneyDiscoverImages.berlin.src,
    previewImageAlt: cityJourneyDiscoverImages.berlin.alt,
    primaryCtaLabel: 'Book a Tour',
    primaryCtaHref: 'https://crescocapitalgroup.my.salesforce-sites.com/InspectionCalendar',
    secondaryCtaLabel: 'Explore Apartments',
    secondaryCtaHref: '/apartments',
    promotionText: 'LIMITED PROMOTION: for Berlin Frankfurter Tor & Mitte-Wedding',
    promotionCtaLabel: 'See promotions',
    promotionCtaHref: '#promotions',
    overview: berlinOverview,
    journey: berlinJourney,
    areaMap: berlinAreaMap,
    communityOperator: berlinCommunityOperator,
    propertyChooser: berlinPropertyChooser,
  },
  cologne: createStubCity('cologne', 'Cologne', {
    areaMapZoom: 11.5,
    communityBody: [
      'Cologne is not just about carnival, it is also welcoming, creative, and above all, a real student city. With over 100,000 students, Cologne is one of the most popular university locations in Germany. Here you will find everything you need to make your studies unforgettable: large universities, international communities, trendy neighborhoods, clubs, cafés, and plenty of Rhine vibes.',
      'Our location on Melatengürtel in Ehrenfeld combines proximity to campus with creative city life – perfect for your everyday life between lectures, the library, and bar nights. Cologne offers you a huge range of courses – from business administration to media, from engineering to sports science.',
    ],
    description:
      'Neon Wood K115 puts you close to Cologne’s universities with furnished apartments and shared community spaces.',
  }),
  frankfurt: createStubCity('frankfurt', 'Frankfurt', {
    areaMapZoom: 11.5,
    communityBody: [
      'Welcome to Mainhatten, the city with the most and highest skyscrapers in Germany. Some of them even belong to the highest buildings in Europe. With 15 skyscrapers over 150m and a total of 36 skyscrapers over 100m, Frankfurt am Main is far from over.',
      "Neon Wood has it's property in Frankfurt Riedberg, directly close within only 2-minutes walking distance to the Goethe University. The banking city of Germany with, amongst others, the headquarters of the European Central Bank, is aiming high and already has another 5 skyscrapers in the planning.",
      'Frankfurt is also flying high in continuing education. With its 10 public and private universities and more than 60,000 students, Frankfurt am Main ranks fifth among the most important university locations in Germany. The Johann Wolfgang Goethe University with its more than 40,000 students is one of the most important universities in the world metropolis. At its 4 locations in Frankfurt, a wide range of disciplines are taught.',
    ],
    description:
      'Live in Riedberg, minutes from Goethe University — modern student housing in Frankfurt’s knowledge hub.',
  }),
  tuebingen: createStubCity('tuebingen', 'Tübingen', {
    areaMapZoom: 12,
    communityBody: [
      "Our newest location is in the heart of Tübingen, one of Germany's most traditional and lively university towns. The University of Tübingen, one of the oldest universities in Europe, is just a few minutes away on foot or by bike - perfect for savouring university life to the full. The city on the Neckar not only offers you a rich academic history, but also a charming old town with narrow streets, picturesque half-timbered houses and a unique atmosphere. This is where historical flair meets modern student life.",
      'Tübingen also has a lively cultural scene with numerous theatres, museums and festivals that provide variety alongside your studies. With around 30,000 students, Tübingen is one of the most important university cities in Germany. The Eberhard Karls University enjoys an international reputation and attracts a large number of students from all over the world every year. Here you can look forward to first-class education and a cosmopolitan community. Do you want to become part of this traditional yet innovative city?',
    ],
    description:
      'Neon Wood in historic Tübingen — walkable university-town living with furnished apartments and community life.',
  }),
}

export function getCityBySlug(slug: string): City | undefined {
  return cities[slug]
}

/** Mock page shell for a city slug, falling back to generic stubs when no fixture exists. */
export function getCityShellBySlug(
  slug: string,
  options: { description?: string; name?: string } = {},
): City {
  const existing = getCityBySlug(slug)
  if (existing) {
    return existing
  }

  const name = options.name ?? slug
  const description = options.description ?? `Student housing in ${name} by Neon Wood.`
  const mapCity = isCityId(slug) ? mapCities.find((entry) => entry.id === slug) : undefined

  return createStubCity(slug, name, {
    areaMapZoom: mapCity?.zoom,
    description,
  })
}

export const citySlugs = Object.keys(cities)
